import { AppDispatch } from "../../store/store";
import { store } from "../../store/store";
import { selectActiveJobs } from "../../store/jobsSlice";
import { api } from "../api";
import {
  moveToNextSubtask,
  completeJob,
  failJob,
  updateJobProgress,
  addExportJobId,
  addUpdateJobId,
  addSyncJobId,
  resetExportJob,
} from "../../store/jobsSlice";
import { setJobFilter } from "../../store/filtersSlice";
import { updateEnrichmentTask2 } from "wasp/client/operations";
import { analytics } from "../../analytics";
import beforeUpdateFormatter from "../beforeUpdateFormatter";
import { setToastError } from "../../store/flowSlice";

class ExportJobService {
  private static instance: ExportJobService;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private dispatch: AppDispatch;

  private constructor(dispatch: AppDispatch) {
    this.dispatch = dispatch;
  }

  static getInstance(dispatch?: AppDispatch): ExportJobService {
    if (!ExportJobService.instance) {
      if (!dispatch) {
        throw new Error(
          "JobService needs dispatch function for initialization"
        );
      }
      ExportJobService.instance = new ExportJobService(dispatch);
    }
    return ExportJobService.instance;
  }

  getCumulativeProgress(step: number, progress: number){
    const totalSteps = 3;
    const totalProgress = 100;
    const progressPerStep = totalProgress / totalSteps;
    return Math.round((step * progressPerStep) + (progress * (progressPerStep / 100)));
  }

  ensurePolling() {
    if (this.pollingInterval) return;

    this.pollingInterval = setInterval(async () => {
      const activeJobs = selectActiveJobs(store.getState());
      // console.log(activeJobs)
      for (const job of Object.values(activeJobs)) {
        // console.group(`Polling job ${job.enrichmentJobId}`);
        try {
          let currentJobId : string | null = null;

          //SubTask 0: update
          //SubTask 1: export
          //SubTask 2: sync

          if (job.currentSubTask === 0) {
            // console.log(`Starting new job ${job.enrichmentJobId}`);

   

            if (!job.updatedFields) {
              this.dispatch(moveToNextSubtask(job.enrichmentJobId));
              return;
            } else {
              if (job.updateJobId) {
                currentJobId = job.updateJobId;
              } else {
                let updateFields = job.updatedFields;

                if (job.exportedFields) {
                  updateFields = updateFields.filter(
                    (field: any) => 
                      job.exportedFields?.includes(field.field_name)
                  );
                }

                if(updateFields.length === 0){
                  this.dispatch(moveToNextSubtask(job.enrichmentJobId));
                  return;
                }

                // console.log(`Fetching update Job Id for ${job.enrichmentJobId}`);
                const { error, success, data } =
                  await api.postUpdateEnrichedCollection({
                    table_id: job.tableId,
                    job_id: job.enrichmentJobId,
                    update_fields: beforeUpdateFormatter(updateFields),
                  });
                if (error) {
                  throw new Error(
                    "Couldn't update the product. " + error.message
                  );
                }
                if (!data?.job_id) {
                  throw new Error("No job ID found in update job response");
                }
                currentJobId = data.job_id;
                await new Promise((resolve) => setTimeout(resolve, 1000));
                // console.log(`Update Job Id for ${job.enrichmentJobId} is ${currentJobId}`);
                this.dispatch(
                  addUpdateJobId({
                    enrichmentJobId: job.enrichmentJobId,
                    updateJobId: currentJobId,
                  })
                );
                await updateEnrichmentTask2({
                  id: job.enrichmentDbId,
                  isExportingToShopify: true,
                  syncedToShopify: false,
                  exportState: "update",
                });
              }
            }
          }

          if (job.currentSubTask === 1) {
            if (job.exportJobId) {
              currentJobId = job.exportJobId;
            } else {
              const { error, success, data } =
                await api.exportEnrichmentToShopify({
                  input_table_id: job.tableId,
                  enrichment_job_id: job.enrichmentJobId,
                  shopify_store_name: job.storeName,
                  fields_to_export: job.exportedFields,
                });
              if (error) {
                throw new Error(
                  "Couldn't export the product. " + error.message
                );
              }
              if (!data?.job_id) {
                throw new Error("No job ID found in export job response");
              }
              currentJobId = data.job_id;
              await new Promise((resolve) => setTimeout(resolve, 1000));
              this.dispatch(
                addExportJobId({
                  enrichmentJobId: job.enrichmentJobId,
                  exportJobId: currentJobId,
                })
              );
              await updateEnrichmentTask2({
                id: job.enrichmentDbId,
                isExportingToShopify: true,
                syncedToShopify: false,
                exportState: "export",
              });
            }
          }

          if (job.currentSubTask === 2) {
            if (job.syncJobId) {
              currentJobId = job.syncJobId;
            } else {
              const { error, success, data } =
                await api.postShopifyToInternalTable({
                  account_id: job.userEmail,
                  shopify_store_name: job.storeName,
                  collection_name: job.collectionName,
                });
              if (error) {
                throw new Error("Couldn't sync the product. " + error.message);
              }
              if (!data?.job_id) {
                throw new Error("No job ID found in sync job response");
              }
              currentJobId = data.job_id;
              await new Promise((resolve) => setTimeout(resolve, 1000));
              this.dispatch(
                addSyncJobId({
                  enrichmentJobId: job.enrichmentJobId,
                  syncJobId: currentJobId,
                })
              );
              await updateEnrichmentTask2({
                id: job.enrichmentDbId,
                isExportingToShopify: true,
                syncedToShopify: false,
                exportState: "sync",
              });
            }
          }

          if (!currentJobId) {
            throw new Error("currentJobId is null");
          }

          const response = await this.checkJobProgress(currentJobId);

          if (response.data?.status === "completed") {
            if (job.currentSubTask < 2) {
              this.dispatch(moveToNextSubtask(job.enrichmentJobId));
            } else {
              this.dispatch(completeJob(job.enrichmentJobId));
              analytics.sendEnhancementJobExportedEvent({
                jobId: job.enrichmentJobId,
                email: job.userEmail,
                destination: "shopify"
              })
              const jobFilter = store.getState().filters.jobFilter;
              if (jobFilter === "running") {
                this.dispatch(setJobFilter("completed"));
              }
              await updateEnrichmentTask2({
                id: job.enrichmentDbId,
                isExportingToShopify: false,
                syncedToShopify: true,
                exportState: "completed",
              });
              //TODO invalidate products
            }
          } else if (response.data?.status === "failed") {
            this.dispatch(
              failJob({
                jobId: job.enrichmentJobId,
                reason: response.data?.message,
              })
            );
            await updateEnrichmentTask2({
              id: job.enrichmentDbId,
              isExportingToShopify: false,
              syncedToShopify: false,
              exportState: "initial",
            });
            this.dispatch(setToastError({
              message: response.data?.message
            }));
          }
          this.dispatch(
            updateJobProgress({
              jobId: job.enrichmentJobId,
              progress: this.getCumulativeProgress(job.currentSubTask, response.data?.progress || 0),
            })
          );
        } catch (error) {
          this.dispatch(
            failJob({
              jobId: job.enrichmentJobId,
              reason:
                error instanceof Error
                  ? error.message
                  : "Unknown error occurred",
            })
          );
          await updateEnrichmentTask2({
            id: job.enrichmentDbId,
            isExportingToShopify: false,
            syncedToShopify: false,
            exportState: "initial",
          });
          this.dispatch(setToastError({
            message: error instanceof Error ? error.message : "Unknown error occurred"
          }));
        }
        finally {
          // console.groupEnd();
        }
      }
    }, 3000);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  private async checkJobProgress(jobId: string) {
    return await api.getJobStatus(jobId);
  }

  async resetExportProgress(enrichmentJobId: string, enrichmentDbId: string) {
    console.log("Resetting export progress for job", enrichmentJobId);
    const job = selectActiveJobs(store.getState())[enrichmentJobId];
    if(job){
      if(job.exportJobId){
        await api.cancelJob(job.exportJobId)
      }
    }
    this.dispatch(resetExportJob(enrichmentJobId));
    localStorage.removeItem(`pendingChanges_${enrichmentJobId}`);
    localStorage.removeItem(`updateRequestSent_${enrichmentJobId}`);
    localStorage.removeItem(`updatedProducts_${enrichmentJobId}`);
    localStorage.removeItem(`updateJobId_${enrichmentJobId}`);
    localStorage.removeItem(`updateJobStarted_${enrichmentJobId}`);
    localStorage.removeItem(`updateJobFinished_${enrichmentJobId}`);
    localStorage.removeItem(`exportJobId_${enrichmentJobId}`);
    localStorage.removeItem(`exportJobStarted_${enrichmentJobId}`);
    localStorage.removeItem(`exportJobFinished_${enrichmentJobId}`);
    localStorage.removeItem(`syncJobId_${enrichmentJobId}`);
    localStorage.removeItem(`syncJobStarted_${enrichmentJobId}`);
    localStorage.removeItem(`syncJobFinished_${enrichmentJobId}`);
    localStorage.removeItem(`exportState_${enrichmentJobId}`);
    await updateEnrichmentTask2({
      id: enrichmentDbId,
      isExportingToShopify: false,
      syncedToShopify: false,
      exportState: "initial",
    });
    // console.log("Export progress reset for job", jobId);
  }
}

ExportJobService.getInstance(store.dispatch);

export { ExportJobService };
